/* eslint-disable react-hooks/exhaustive-deps */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import Tooltip from '../../components/Price/Tooltip'
import drawRooms from '../../drawing/drawRooms'
import { isTouchDevice } from '../../components/MediaQuery'
import { useIsMobile } from '../../components/MediaQuery/Mobile'
import openModal from '../../actions/Modals/openModal'
import drawSummaryText from '../../drawing/drawSummaryText'
import { createCacheCanvas } from '../../utils/Canvas'
import { Modals } from '../Modals/ModalContainer'
import closeModal from '../../actions/Modals/closeModal'
import { UpdateRoomModal } from '../BookingCalendar/RoomModal'
import RoomForms from '../Form/CreateRoom'
import { imageCacheService } from '../../drawing/images/imageCache'
import { getAirbnbSync } from '../../reducers/airbnb/selectors'

const RoomCanvas = ({
  width,
  height,
  constants,
  dpConstants,
  size,
  rooms,
  startRoomIdx,
  scrollTop,
  style,
  closeEditRoomModal,
  openEditRoomModal,
  withSummary,
  scrollTopOffset = 0,
}) => {
  const [t] = useTranslation()
  const isMobile = useIsMobile()
  const [canvas, setCanvas] = useState(null)
  const [bufferCanvas, setBufferCanvas] = useState(null)
  const [summaryCache, setSummaryCache] = useState(null)
  const [hoveredRoom, setHoveredRoom] = useState(null)
  const [wrapperRef, setWrapperRef] = useState(null)
  const [wrapperRect, setWrapperRect] = useState(null)
  const summaryLabel = useMemo(() => t('summary'), [t])
  const sync = useSelector(getAirbnbSync)

  useEffect(() => {
    setHoveredRoom(null)
  }, [rooms])

  useEffect(() => {
    setSummaryCache(
      createCacheCanvas(
        ctx => drawSummaryText(ctx, dpConstants, summaryLabel),
        dpConstants.ROOM_WIDTH,
        dpConstants.SUMMARY_HEIGHT,
      ),
    )
  }, [dpConstants, summaryLabel])

  useEffect(() => {
    if (bufferCanvas) {
      bufferCanvas.height =
        height * dpConstants.PIXEL_RATIO + dpConstants.GRID_HEIGHT
      bufferCanvas.width = width * dpConstants.PIXEL_RATIO
    }
    if (canvas) {
      canvas.height = height * dpConstants.PIXEL_RATIO
      canvas.width = width * dpConstants.PIXEL_RATIO
    }
    if (wrapperRef) {
      setWrapperRect(wrapperRef.getBoundingClientRect())
    }
  }, [wrapperRef, width, height, constants, dpConstants])

  useEffect(() => {
    let bc = bufferCanvas
    if (!bc) {
      bc = document.createElement('canvas')
      bc.height = height * dpConstants.PIXEL_RATIO + dpConstants.GRID_HEIGHT
      bc.width = width * dpConstants.PIXEL_RATIO

      setBufferCanvas(bc)
    }
    const ctx = bc.getContext('2d')
    ctx.clearRect(0, 0, bc.width, bc.height)

    drawRooms(ctx, dpConstants, bc.height, rooms, startRoomIdx, sync)
  }, [bufferCanvas, sync, width, height, rooms, startRoomIdx, dpConstants])

  const doTheDrawing = () => {
    if (canvas) {
      const yOffset = Math.round(
        (scrollTop * dpConstants.PIXEL_RATIO) % dpConstants.GRID_HEIGHT,
      )
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(
        bufferCanvas,
        0,
        yOffset,
        canvas.width,
        canvas.height,
        0,
        0,
        canvas.width,
        canvas.height,
      )

      if (withSummary && summaryCache) {
        ctx.drawImage(
          summaryCache,
          0,
          0,
          canvas.width,
          dpConstants.SUMMARY_HEIGHT,
          0,
          canvas.height - dpConstants.SUMMARY_HEIGHT,
          canvas.width,
          dpConstants.SUMMARY_HEIGHT,
        )
      }
    }
  }

  useEffect(() => {
    imageCacheService.addObserver(() => {
      doTheDrawing()
    })
  }, [bufferCanvas])

  useEffect(() => {
    doTheDrawing()
  }, [
    canvas,
    bufferCanvas,
    width,
    height,
    rooms,
    dpConstants,
    scrollTop,
    withSummary,
    summaryCache,
  ])

  const roomTooltipRefObject = useCallback(() => {
    if (!wrapperRect) {
      return {}
    }

    return {
      clientHeight: constants.GRID_HEIGHT,
      clientWidth: constants.ROOM_WIDTH,
      getBoundingClientRect: () => ({
        bottom:
          wrapperRect.top +
          hoveredRoom.y +
          constants.GRID_HEIGHT -
          scrollTopOffset,
        height: constants.GRID_HEIGHT,
        left: wrapperRect.left,
        right: wrapperRect.left + constants.ROOM_WIDTH,
        top: wrapperRect.top + hoveredRoom.y - scrollTopOffset,
        width: constants.ROOM_WIDTH,
      }),
    }
  }, [constants, hoveredRoom, wrapperRect, scrollTopOffset])

  const mouseMoveListener = e => {
    if (wrapperRect && rooms.length) {
      const y = e.clientY - wrapperRect.top + scrollTopOffset
      const yOffset = scrollTop % constants.GRID_HEIGHT
      const idx = Math.floor((y + yOffset) / constants.GRID_HEIGHT)

      if (idx >= 0 && startRoomIdx + idx < rooms.length) {
        setHoveredRoom({
          room: rooms[startRoomIdx + idx],
          y: idx * constants.GRID_HEIGHT - yOffset,
        })
      }
    } else {
      setHoveredRoom(null)
    }
  }

  const clickListener = e => {
    const y = e.clientY - wrapperRect.top + scrollTopOffset
    const yOffset = scrollTop % constants.GRID_HEIGHT
    const idx = Math.floor((y + yOffset) / constants.GRID_HEIGHT)

    if (idx >= 0 && startRoomIdx + idx < rooms.length) {
      setHoveredRoom(null)
      const room = rooms[startRoomIdx + idx]
      openEditRoomModal(
        <UpdateRoomModal close={closeEditRoomModal} roomId={room.id}>
          <RoomForms.Update roomId={room.id} />
        </UpdateRoomModal>,
      )
    }
  }

  const mouseLeaveListener = () => {
    setHoveredRoom(null)
  }

  return (
    <div
      style={{
        height: height,
        width: width,
        zIndex: 1,
        ...style,
      }}
      ref={setWrapperRef}
      onMouseMove={mouseMoveListener}
      onMouseLeave={mouseLeaveListener}
      onClick={clickListener}
    >
      <canvas
        className={'RoomCanvas'}
        ref={setCanvas}
        width={width * constants.PIXEL_RATIO}
        height={height * constants.PIXEL_RATIO}
        style={{
          background: '#fff',
          height: `${height}px`,
          position: 'absolute',
          width: `${width}px`,
        }}
      />
      <Tooltip
        placement={'bottom'}
        size={size}
        arrow={true}
        title={
          hoveredRoom && hoveredRoom.room ? hoveredRoom.room.description : null
        }
        isRoom={true}
        fakeRef={roomTooltipRefObject}
        disableHoverListener={true}
        open={
          !!hoveredRoom &&
          !!hoveredRoom.room &&
          hoveredRoom.room.description &&
          !isTouchDevice &&
          !isMobile
        }
      >
        <div />
      </Tooltip>
    </div>
  )
}

const mapDispatchToProps = dispatch => ({
  closeEditRoomModal: () => dispatch(closeModal(Modals.EditRoom)),
  openEditRoomModal: element => dispatch(openModal(Modals.EditRoom, element)),
})

export default connect(
  null,
  mapDispatchToProps,
)(RoomCanvas)
